/**
 * Convert a Markdown report to a Blob that Microsoft Word (and Google Docs /
 * LibreOffice / Pages) opens as a proper Word document.
 *
 * Implementation: produce a Word-HTML document (the same format Outlook uses
 * for rich emails). Word recognises the `urn:schemas-microsoft-com:office`
 * namespace and MIME type `application/msword`, opens the file, and offers to
 * save as .docx. This avoids adding a multi-hundred-KB docx-generation
 * dependency to the bundle.
 *
 * The markdown subset we support matches what the backend generator emits
 * today: headings (#, ##, ###), blockquotes (>), horizontal rules (---),
 * bold (**x**) and italics (*x*), numbered list items (`1. foo`) and bullets
 * (`- foo` / `* foo`), and plain paragraphs. No tables, images, or code
 * blocks — the LLM prompt doesn't produce those.
 *
 * List items render as their own `<p>` rather than being joined into a single
 * paragraph, so references and bullet recommendations keep one item per line
 * in Word.
 */

export function markdownToWordBlob(markdown: string, title: string): Blob {
  const body = markdownToHtml(markdown);
  const safeTitle = escapeHtml(title);
  const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<title>${safeTitle}</title>
<!--[if gte mso 9]>
<xml>
  <w:WordDocument>
    <w:View>Print</w:View>
    <w:Zoom>100</w:Zoom>
    <w:DoNotOptimizeForBrowser/>
  </w:WordDocument>
</xml>
<![endif]-->
<style>
  @page { margin: 1in; }
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #1a202c; line-height: 1.5; }
  h1 { font-size: 22pt; color: #1a202c; margin: 0 0 12pt 0; }
  h2 { font-size: 16pt; color: #2d3748; margin: 18pt 0 8pt 0; }
  h3 { font-size: 13pt; color: #2d3748; margin: 14pt 0 6pt 0; }
  p  { margin: 0 0 10pt 0; }
  p.li { margin: 0 0 6pt 18pt; text-indent: -18pt; }
  blockquote { color: #4a5568; border-left: 3pt solid #cbd5e0; padding-left: 10pt; margin: 10pt 0; font-style: italic; }
  hr { border: 0; border-top: 1pt solid #e2e8f0; margin: 14pt 0; }
  strong { font-weight: 700; }
  em { font-style: italic; }
</style>
</head>
<body>
${body}
</body>
</html>`;
  return new Blob([html], { type: 'application/msword' });
}

function markdownToHtml(md: string): string {
  const lines = md.replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(' ').trim();
    if (text) out.push(`<p>${inline(text)}</p>`);
    paragraph = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushParagraph(); continue; }

    if (/^---+$/.test(line.trim())) { flushParagraph(); out.push('<hr/>'); continue; }

    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      flushParagraph();
      const level = h[1].length;
      out.push(`<h${level}>${inline(h[2])}</h${level}>`);
      continue;
    }

    const bq = /^>\s?(.*)$/.exec(line);
    if (bq) {
      flushParagraph();
      out.push(`<blockquote>${inline(bq[1])}</blockquote>`);
      continue;
    }

    // Numbered list item (references, improvement steps, etc.) — preserve the
    // visible number and give the item its own paragraph so each reference
    // lands on its own line in Word instead of being joined with the next.
    const ol = /^(\d+)\.\s+(.*)$/.exec(line);
    if (ol) {
      flushParagraph();
      out.push(`<p class="li">${ol[1]}. ${inline(ol[2])}</p>`);
      continue;
    }

    // Bulleted list item (- or *). Dash rendered as a real bullet.
    const ul = /^[-*]\s+(.*)$/.exec(line);
    if (ul) {
      flushParagraph();
      out.push(`<p class="li">• ${inline(ul[1])}</p>`);
      continue;
    }

    paragraph.push(line);
  }
  flushParagraph();
  return out.join('\n');
}

function inline(text: string): string {
  // Escape first, then apply emphasis so user-supplied < / > don't break HTML.
  let s = escapeHtml(text);
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, '$1<em>$2</em>');
  return s;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Filename-safe version of a report title. */
export function titleToFilename(title: string): string {
  return (title || 'report')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 80) || 'report';
}
